import type { FC } from 'react';
import { Link, useBreakpointValue } from '@chakra-ui/react';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { ShopperProductsTypes } from 'commerce-sdk-isomorphic';
import { BREAKPOINTS_PX } from '../../theme/foundations/breakpoints';
import { Text } from '../../ui-components/typography/text';
import { classNames } from '../../utils/tailwind/classNames';
import { GetIcon } from '../icons';
import styles from './CategoryImageTiles.module.scss';

export type Category = ShopperProductsTypes.Category & {
    c_productInfoTile?: string[];
    c_template?: string;
    c_href: string;
    c_slotBannerImage: string;
};

export type CategoryImageTilesProps = {
    items: Category[];
    dataTestId?: string;
};

/**
 * This component is used to render a list of categories as image tiles.
 *
 * @param calloutMsg - The message to display above the tiles.
 * @param items - The list of categories to render.
 * @returns A VStack with the callout message and the tiles.
 */
const CategoryImageTiles: FC<CategoryImageTilesProps> = ({ items, dataTestId }) => {
    const isMobile = useBreakpointValue({ base: true, md: false });
    const showNavigation = useBreakpointValue({ base: false, md: true });

    return (
        <div
            className={classNames(
                styles.categoryImageTiles,
                'w-full h-auto overflow-hidden relative',
            )}
            data-testid={dataTestId}
        >
            <Swiper
                modules={[Navigation]}
                slidesPerView="auto"
                spaceBetween={16}
                navigation={
                    showNavigation
                        ? {
                              nextEl: '.swiper-button-next',
                              prevEl: '.swiper-button-prev',
                          }
                        : false
                }
                breakpoints={{
                    [BREAKPOINTS_PX.md]: {
                        spaceBetween: 24,
                    },
                }}
            >
                {items.map((item: Category) => (
                    <SwiperSlide key={item.id}>
                        {isMobile ? (
                            // Mobile variant with image on the left
                            <Link
                                className="group flex h-auto min-h-[72px] w-full items-center gap-2 rounded-lg border border-slt-grey-light bg-slt-grey-light p-2 transition-all hover:border hover:border-slt-pepper-300"
                                href={item.c_href}
                                _hover={{ textDecoration: 'none' }}
                            >
                                {item.c_slotBannerImage && (
                                    <img
                                        className="h-14 w-14 shrink-0 object-contain contrast-[90%] "
                                        src={item.c_slotBannerImage}
                                        alt={item.name}
                                    />
                                )}
                                <Text
                                    className="text-slt-pepper-1000 group-hover:underline"
                                    size="small"
                                    style="bold"
                                >
                                    {item.name}
                                </Text>
                            </Link>
                        ) : (
                            // Desktop variant - original design
                            <Link
                                className="group flex aspect-square flex-col items-center gap-2"
                                key={item.id}
                                href={item.c_href}
                                _hover={{ textDecoration: 'none' }}
                            >
                                <figure className="flex h-[200px] w-[200px] flex-col gap-2 overflow-hidden">
                                    <div className="flex h-[128px] justify-center rounded-lg border border-slt-grey-light bg-slt-grey-light p-2 transition-colors group-hover:border group-hover:border-slt-pepper-300">
                                        <img
                                            className="aspect-square h-full object-contain contrast-[90%]"
                                            src={item.c_slotBannerImage}
                                            alt={item.name}
                                        />
                                    </div>
                                    <Text
                                        className="flex flex-[1] group-hover:underline"
                                        size="small"
                                        style="bold"
                                        as="figcaption"
                                    >
                                        {item.name}
                                    </Text>
                                </figure>
                            </Link>
                        )}
                    </SwiperSlide>
                ))}
            </Swiper>
            {showNavigation && (
                <>
                    <div className="swiper-button-next">
                        <GetIcon name="navigation-slide-arrow-right" size="md" />
                    </div>
                    <div className="swiper-button-prev">
                        <GetIcon name="navigation-slide-arrow-left" size="md" />
                    </div>
                </>
            )}
        </div>
    );
};

CategoryImageTiles.defaultProps = {
    dataTestId: undefined,
};

export { CategoryImageTiles };
